// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Lock } from 'lucide-react';
import { useAuth } from '../App';

// Calculator components
import PACSimpleCalc      from '../components/calculators/PAC';
import BudgetCalc         from '../components/calculators/BudgetMensile';
import EmergencyCalc      from '../components/calculators/FondoEmergenza';
import SavingsCalc        from '../components/calculators/SimulatoreRisparmio';
import MutuoCalc          from '../components/calculators/Mutuo';
import StipendioCalc      from '../components/calculators/StipendioNetto';
import InflazioneCalc     from '../components/calculators/ErosioneInflazione';
import FIRECalc           from '../components/calculators/SimulatoreFIRE';
import TFRCalc            from '../components/calculators/CalcolatoreTFR';
import ISEECalc           from '../components/calculators/SimulatoreISEE';
import DetrazioniCalc     from '../components/calculators/RisparmioFiscale730';
import PACAvanzatoCalc    from '../components/calculators/PACAvanzato';
import PensioneCalc       from '../components/calculators/PensioneComplementare';
import CostoFondiCalc     from '../components/calculators/CostoRealeFondi';
import SurrogaCalc        from '../components/calculators/SurrogaMutuo';
import AffittoVsMutuoCalc from '../components/calculators/AffittoVsMutuo';
import LeasingCalc        from '../components/calculators/LeasingVsAcquisto';
import LeasingAutoCalc    from '../components/calculators/LeasingAuto';
import CartaRevolvingCalc from '../components/calculators/CartaRevolving';
import CONSAPCalc         from '../components/calculators/CONSAP';
import CoperturaVitaCalc  from '../components/calculators/CoperturaVita';
import PensioneINPSCalc   from '../components/calculators/PensioneINPS';
import PartitaIVACalc     from '../components/calculators/PartitaIVA';
import TrackerAbbonamentiCalc from '../components/calculators/TrackerAbbonamenti';
import ObiettivoCalc      from '../components/calculators/ObiettivoFinanziario';
import FinanziamentoAutoCalc from '../components/calculators/FinanziamentoAuto';
import StipendioEuropaCalc  from '../components/calculators/StipendioEuropa';
import AssegnoUnicoCalc     from '../components/calculators/AssegnoUnico';
import ImpostaSuccessioneCalc from '../components/calculators/ImpostaSuccessione';
// Sprint 18 new calculators
import PrestitorPersonaleCalc from '../components/calculators/PrestitorPersonale';
import CessioneQuintoCalc from '../components/calculators/CessioneQuinto';
import NASpICalc          from '../components/calculators/NASpI';
import MaternitaCalc      from '../components/calculators/Maternita';
import InteressiMoraCalc  from '../components/calculators/InteressiMora';
// Deployment 11
import RivalutazioneISTATCalc from '../components/calculators/RivalutazioneISTAT';
// Deployment 12
import LettoreBustaPagaCalc  from '../components/calculators/LettoreBustaPaga';
// Deployment 14
import CryptoImposteCalc     from '../components/calculators/CryptoImposte';
// Deployment 23 — Sezione G: BFP
import BuoniFruttiferiPostaliCalc from '../components/calculators/BuoniFruttiferiPostali';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── TABS DEFINITION ──────────────────────────────────────────────────────────
const TABS = [
  { id: 'risparmio',   label: '💰 Risparmio e budget' },
  { id: 'investimenti', label: '📈 Investimenti' },
  { id: 'casa',        label: '🏠 Casa e mutuo' },
  { id: 'lavoro',      label: '💼 Lavoro e reddito' },
  { id: 'previdenza',  label: '👴 Previdenza' },
  { id: 'protezione',  label: '🛡️ Protezione' },
  { id: 'acquisti',    label: '🚗 Grandi acquisti' },
  { id: 'credito',     label: '🏦 Credito e lavoro' },
];

// ── CALCS REGISTRY ───────────────────────────────────────────────────────────
const CALCS = [
  // Tab 1 — Risparmio e budget
  { id: 'budget',       slug: 'budget-mensile',       tab: 'risparmio',    icon: '💰', label: 'Budget mensile',           subtitle: 'Analizza entrate e uscite',               Component: BudgetCalc,          premium: false },
  { id: 'emergency',    slug: 'fondo-emergenza',       tab: 'risparmio',    icon: '🔐', label: 'Fondo emergenza',          subtitle: 'Quanti mesi sei coperto?',                Component: EmergencyCalc,       premium: false },
  { id: 'inflazione',   slug: 'erosione-inflazione',   tab: 'risparmio',    icon: '📉', label: 'Erosione inflazione',      subtitle: 'Quanto perdi tenendo i soldi fermi?',     Component: InflazioneCalc,      premium: false },
  { id: 'revolving',    slug: 'carta-revolving',       tab: 'risparmio',    icon: '💳', label: 'Carta revolving',          subtitle: 'Quanto ti costa davvero?',                Component: CartaRevolvingCalc,  premium: false },
  { id: 'tracker',      slug: 'tracker-abbonamenti',   tab: 'risparmio',    icon: '📋', label: 'Tracker abbonamenti',      subtitle: 'Quanto spendi in abbonamenti?',           Component: TrackerAbbonamentiCalc, premium: false },
  { id: 'obiettivo',    slug: 'obiettivo',             tab: 'risparmio',    icon: '🎯', label: 'Simulatore obiettivo',     subtitle: 'Quanto devi risparmiare?',                Component: ObiettivoCalc,       premium: false },
  { id: 'istat',        slug: 'rivalutazione-istat',   tab: 'risparmio',    icon: '📊', label: 'Rivalutazione ISTAT',      subtitle: 'Rivaluta importi con indici FOI',         Component: RivalutazioneISTATCalc, premium: false },
  { id: 'bfp',          slug: 'buoni-fruttiferi-postali', tab: 'risparmio', icon: '📮', label: 'Buoni Fruttiferi Postali', subtitle: 'Rendimento netto BFP vs BTP, conto deposito, liquidità', Component: BuoniFruttiferiPostaliCalc, premium: false },
  // Tab 2 — Investimenti
  { id: 'pac',          slug: 'pac',                   tab: 'investimenti', icon: '📈', label: 'PAC',                     subtitle: 'Simula la crescita mensile',              Component: PACSimpleCalc,       premium: false, step: 3 },
  { id: 'fire',         slug: 'simulatore-fire',       tab: 'investimenti', icon: '🔥', label: 'Simulatore FIRE',          subtitle: 'Calcola la tua libertà finanziaria',      Component: FIRECalc,            premium: false, step: 4 },
  { id: 'pac-avanzato', slug: 'pac-avanzato',          tab: 'investimenti', icon: '📊', label: 'PAC avanzato',             subtitle: 'ETF, crypto e portafogli reali',          Component: PACAvanzatoCalc,     premium: true  },
  { id: 'fondi',        slug: 'costo-reale-fondi',     tab: 'investimenti', icon: '💸', label: 'Costo reale dei fondi',    subtitle: 'Quanto paghi di commissioni?',            Component: CostoFondiCalc,      premium: true  },
  { id: 'crypto-imposte', slug: 'crypto-imposte',      tab: 'investimenti', icon: '🪙', label: 'Crypto Imposte 2026',      subtitle: 'L. 207/2024 — soglia abolita, 33% / 26%', Component: CryptoImposteCalc,   premium: false },
  // Tab 3 — Casa e mutuo
  { id: 'mutuo',        slug: 'mutuo',                 tab: 'casa',         icon: '🏠', label: 'Simulatore mutuo',         subtitle: 'Calcola rata e costi reali',              Component: MutuoCalc,           premium: false },
  { id: 'surroga',      slug: 'surroga-mutuo',         tab: 'casa',         icon: '🔄', label: 'Surroga mutuo',            subtitle: 'Conviene cambiare banca?',                Component: SurrogaCalc,         premium: true  },
  { id: 'affitto',      slug: 'affitto-vs-mutuo',      tab: 'casa',         icon: '⚖️', label: 'Affitto vs Mutuo',         subtitle: 'Cosa conviene sul lungo periodo?',        Component: AffittoVsMutuoCalc,  premium: true  },
  { id: 'consap',       slug: 'consap',                tab: 'casa',         icon: '🏛️', label: 'CONSAP',                   subtitle: 'Hai diritto alla garanzia statale?',      Component: CONSAPCalc,          premium: true  },
  // Tab 4 — Lavoro e reddito
  { id: 'stipendio',    slug: 'stipendio-netto',       tab: 'lavoro',       icon: '💼', label: 'Stipendio netto',          subtitle: 'Dalla RAL al netto reale',                Component: StipendioCalc,       premium: false },
  { id: 'tfr',          slug: 'tfr',                   tab: 'lavoro',       icon: '📄', label: 'Calcolatore TFR',          subtitle: 'Azienda o fondo pensione?',               Component: TFRCalc,             premium: false },
  { id: 'detrazioni',   slug: 'detrazioni-730',        tab: 'lavoro',       icon: '🧾', label: 'Risparmio fiscale 730',    subtitle: 'Quanto recuperi con il 730?',             Component: DetrazioniCalc,      premium: false },
  { id: 'piva',         slug: 'partita-iva',           tab: 'lavoro',       icon: '🧾', label: 'Calcolatore P.IVA',        subtitle: 'Quanto devi fatturare?',                  Component: PartitaIVACalc,      premium: false },
  { id: 'lettore-busta', slug: 'lettore-busta-paga',   tab: 'lavoro',       icon: '🧾', label: 'Lettore Busta Paga',       subtitle: 'Calcola il netto in totale anonimato',     Component: LettoreBustaPagaCalc, premium: false },
  // Tab 5 — Previdenza
  { id: 'pensione',     slug: 'pensione-complementare', tab: 'previdenza',  icon: '💎', label: 'Pensione complementare',   subtitle: 'Colma il gap pensionistico',              Component: PensioneCalc,        premium: true  },
  { id: 'pensione-inps', slug: 'pensione-inps',        tab: 'previdenza',   icon: '🏛️', label: 'Pensione INPS stimata',    subtitle: 'Quanto prenderai di pensione?',           Component: PensioneINPSCalc,    premium: false },
  { id: 'assegno-unico', slug: 'assegno-unico',        tab: 'previdenza',   icon: '👶', label: 'Assegno Unico 2026',       subtitle: 'A quanto hai diritto?',                   Component: AssegnoUnicoCalc,    premium: false },
  { id: 'isee',         slug: 'isee',                  tab: 'previdenza',   icon: '👨‍👩‍👧', label: 'Simulatore ISEE',          subtitle: 'Quali agevolazioni puoi ottenere?',       Component: ISEECalc,            premium: false },
  // Tab 6 — Protezione
  { id: 'copertura',    slug: 'copertura-vita',        tab: 'protezione',   icon: '🛡️', label: 'Confronto Assicurazioni',   subtitle: 'Confronta polizze vita, casa, RC e altre coperture',        Component: CoperturaVitaCalc,   premium: false },
  { id: 'successione',  slug: 'imposta-successione',   tab: 'protezione',   icon: '🏛️', label: 'Imposta successione',      subtitle: 'Quanto pagheranno i tuoi eredi?',         Component: ImpostaSuccessioneCalc, premium: false },
  // Tab 7 — Grandi acquisti
  { id: 'leasing-auto', slug: 'leasing-auto',          tab: 'acquisti',     icon: '🚗', label: 'Leasing Auto',             subtitle: 'Leasing vs Finanziamento vs Cash',        Component: LeasingAutoCalc,     premium: false },
  { id: 'leasing',      slug: 'leasing-vs-acquisto',   tab: 'acquisti',     icon: '🔄', label: 'Leasing vs Acquisto (old)',subtitle: 'Confronto costo netto reale',             Component: LeasingCalc,         premium: true  },
  { id: 'fin-auto',     slug: 'finanziamento-auto',    tab: 'acquisti',     icon: '🚘', label: 'Finanziamento Auto',       subtitle: 'Quanto ti costa davvero l\'auto a rate?', Component: FinanziamentoAutoCalc, premium: false },
  // Tab 8 — Credito e lavoro (Sprint 18)
  { id: 'prestito',     slug: 'prestito-personale',    tab: 'credito',      icon: '💳', label: 'Prestito personale',       subtitle: 'Rata, TAEG e piano ammortamento',         Component: PrestitorPersonaleCalc, premium: false },
  { id: 'cessione',     slug: 'cessione-quinto',       tab: 'credito',      icon: '📑', label: 'Cessione del quinto',      subtitle: 'Quanto puoi cedere del tuo stipendio?',   Component: CessioneQuintoCalc,  premium: false },
  { id: 'naspi',        slug: 'naspi',                 tab: 'credito',      icon: '🏛️', label: 'NASpI 2026',               subtitle: 'Simula la tua indennità disoccupazione',  Component: NASpICalc,           premium: false },
  { id: 'maternita',    slug: 'maternita',             tab: 'credito',      icon: '👶', label: 'Maternità / Paternità',    subtitle: 'Indennità INPS e congedo parentale',      Component: MaternitaCalc,       premium: false },
  { id: 'mora',         slug: 'interessi-mora',        tab: 'credito',      icon: '⏰', label: 'Interessi di mora',        subtitle: 'Calcola i costi del ritardo nei pagamenti',Component: InteressiMoraCalc,   premium: false },
  // Tab 4 — Lavoro (extra)
  { id: 'stipendio-eu', slug: 'stipendio-europa',      tab: 'lavoro',       icon: '🌍', label: 'Stipendio in Europa',      subtitle: 'Quanto vale il tuo stipendio in UE?',     Component: StipendioEuropaCalc, premium: false },
  // Legacy — kept for URL backward compat, NOT shown in any tab
  { id: 'savings',      slug: 'simulatore-risparmio',  tab: null,           icon: '🎯', label: 'Simulatore risparmio',     subtitle: 'Raggiungi il tuo obiettivo',              Component: SavingsCalc,         premium: false },
];

const findCalcBySlug = (slug) => {
  // Slug aliases (Deployment 13): legacy/alias slugs map to canonical calc.
  const ALIAS = { 'busta-paga': 'lettore-busta-paga' };
  const target = ALIAS[slug] || slug;
  return CALCS.find(c => c.slug === target || c.id === target);
};

// ── MiFID II / Calculator result disclaimer (P2 — Deployment 12, aggiornato D26) ─────────────
// Mostrato sotto il risultato di OGNI calcolatore (eccetto le 3 eccezioni non-finanziarie).
// ISEE ha un disclaimer specifico (stima vs DSU). Testo aggiornato a Maggio 2026 (v2.1).
// I 3 calcolatori soggetti a frequenti variazioni normative (successione, crypto, pensione
// complementare) mostrano una riga aggiuntiva con la data di aggiornamento e i link alle
// fonti ufficiali (agenziaentrate.gov.it, inps.it).
const DISCLAIMER_SKIP = new Set(['rivalutazione-istat', 'tracker-abbonamenti', 'budget-mensile']);
const DISCLAIMER_ISEE_SLUG = 'isee';
const DISCLAIMER_LEGAL_UPDATE_SLUGS = new Set([
  // Deployment 26 — slug fiscalmente sensibili (data aggiornamento + link fonti ufficiali)
  'imposta-successione', 'crypto-imposte', 'pensione-complementare',
  // Deployment 27 — estensione ai 24 calcolatori con parametri fiscali/normativi variabili
  // Casa
  'mutuo', 'surroga-mutuo', 'affitto-vs-mutuo', 'consap',
  // Acquisti
  'leasing-vs-acquisto', 'finanziamento-auto',
  // Debiti
  'prestito-personale', 'cessione-del-quinto', 'leasing-auto', 'interessi-mora',
  // Investimenti
  'pac', 'simulatore-fire', 'pac-avanzato', 'costo-reale-fondi',
  // Lavoro
  'stipendio-netto', 'tfr', 'detrazioni-730', 'partita-iva',
  'stipendio-europa', 'naspi', 'maternita',
  // Previdenza
  'pensione-inps', 'assegno-unico',
  // Protezione
  'copertura-vita',
  // Risparmio
  'carta-revolving', 'buoni-fruttiferi-postali',
]);

const CalcResultDisclaimer = ({ calc }) => {
  if (!calc || DISCLAIMER_SKIP.has(calc.slug)) return null;
  if (calc.slug === DISCLAIMER_ISEE_SLUG) {
    return (
      <div className="mt-6 p-4 rounded-xl text-xs leading-relaxed" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)' }} data-testid="calc-disclaimer-isee">
        <p className="font-semibold text-[#C9A84C] mb-1">Avvertenza ISEE</p>
        <p className="text-[#888780]">
          Questa è una <strong className="text-[#E8E8E4]">stima indicativa</strong> dell'ISEE. L'ISEE ufficiale può essere richiesto solo presentando la <strong className="text-[#E8E8E4]">DSU (Dichiarazione Sostitutiva Unica)</strong> tramite un <strong className="text-[#E8E8E4]">CAF</strong> o accedendo al <a href="https://www.inps.it" target="_blank" rel="noopener noreferrer" className="text-[#C9A84C] underline">portale INPS.it</a>. I valori reali possono differire in funzione di patrimonio, redditi e composizione del nucleo familiare.
        </p>
      </div>
    );
  }
  const showLegalUpdate = DISCLAIMER_LEGAL_UPDATE_SLUGS.has(calc.slug);
  return (
    <div className="mt-6 p-4 rounded-xl text-xs leading-relaxed" style={{ background: 'rgba(232,144,58,0.08)', border: '1px solid rgba(232,144,58,0.25)' }} data-testid="calc-disclaimer">
      <p className="font-semibold text-[#E8903A] mb-2">⚠️ Nota legale</p>
      <p className="text-[#888780]">
        I contenuti e i calcolatori di SAPIENS hanno esclusivamente finalità educativa e informativa generale. Non costituiscono consulenza finanziaria, fiscale, legale o di investimento ai sensi del <strong className="text-[#E8E8E4]">D.Lgs. 58/1998 (TUF)</strong> e della <strong className="text-[#E8E8E4]">Direttiva MiFID II (2014/65/UE)</strong>. I risultati sono stime matematiche basate sui parametri inseriti e non tengono conto della situazione personale dell'utente. Prima di prendere decisioni finanziarie consulta un professionista abilitato.
      </p>
      {showLegalUpdate && (
        <p className="text-[#888780] mt-2" data-testid="calc-disclaimer-update">
          <strong className="text-[#E8E8E4]">Aggiornato a:</strong> Maggio 2026 — La normativa è soggetta a variazioni. Verifica sempre: <a href="https://www.agenziaentrate.gov.it" target="_blank" rel="noopener noreferrer" className="text-[#C9A84C] underline">agenziaentrate.gov.it</a>, <a href="https://www.inps.it" target="_blank" rel="noopener noreferrer" className="text-[#C9A84C] underline">inps.it</a>.
        </p>
      )}
    </div>
  );
};

// ── CALCULATOR CARD ──────────────────────────────────────────────────────────
const CalcCard = ({ calc, onClick, isPreview }) => {
  const showLock = calc.premium && !isPreview;
  return (
    <motion.button
      onClick={() => onClick(calc)}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ borderColor: 'rgba(201,168,76,0.5)' }}
      className="w-full text-left transition-colors"
      style={{ background: '#252523', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: 16, cursor: 'pointer', position: 'relative' }}
      data-testid={`calc-card-${calc.id}`}
    >
      {calc.premium && (
        <span className="absolute top-2 right-2 text-[10px] font-medium px-1.5 py-0.5 rounded" style={{ background: '#C9A84C', color: '#3A2800' }}>
          Premium
        </span>
      )}
      {showLock && (
        <div className="absolute inset-0 rounded-xl flex items-center justify-center" style={{ background: 'rgba(28,28,26,0.5)' }}>
          <Lock className="w-4 h-4 text-[#888780] opacity-60" />
        </div>
      )}
      <div className="text-2xl mb-2">{calc.icon}</div>
      <p className="font-medium text-[#E8E8E4] leading-tight mb-1" style={{ fontSize: 14 }}>{calc.label}</p>
      <p data-testid="calc-description" style={{ fontSize: 12, marginTop: 4, color: 'var(--sapiens-accent, #d4a843)', fontWeight: 700 }}>{calc.subtitle}</p>
    </motion.button>
  );
};

// ── MAIN PAGE ────────────────────────────────────────────────────────────────
const CalcolatoriPage = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { calcSlug } = useParams();
  const isPreview = sessionStorage.getItem('admin_preview_premium') === 'true';

  const getInitialTab = () => {
    try { return localStorage.getItem('sapiens_calc_tab') || 'risparmio'; } catch { return 'risparmio'; }
  };
  const [activeTab, setActiveTab] = useState(getInitialTab);

  const resolveActive = () => {
    if (calcSlug) { const c = findCalcBySlug(calcSlug); if (c) return c.id; }
    const qCalc = searchParams.get('calc');
    if (qCalc) { const c = findCalcBySlug(qCalc); if (c) return c.id; }
    return null;
  };
  const [active, setActive] = useState(resolveActive);

  useEffect(() => {
    const newId = resolveActive();
    setActive(newId);
    if (newId) {
      const c = CALCS.find(c => c.id === newId);
      if (c && c.tab) setActiveTab(c.tab);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calcSlug, searchParams]);

  const switchTab = (tabId) => {
    setActiveTab(tabId);
    try { localStorage.setItem('sapiens_calc_tab', tabId); } catch {}
    setActive(null);
    navigate('/calcolatori');
  };

  const handleSelect = (calc) => {
    if (calc.premium && !isPreview && !user) {
      navigate('/premium');
      return;
    }
    setActive(calc.id);
    navigate(`/calcolatori/${calc.slug}`);
    if (window.gtag) window.gtag('event', 'calculator_used', { calculator_name: calc.label, user_type: user ? 'registered' : 'guest' });
  };

  const importoFromUrl = searchParams.get('importo');
  const markOnboardingStep = useCallback(async (stepNum) => {
    const ssKey = `sap_ob_step_${stepNum}_done`;
    if (sessionStorage.getItem(ssKey)) return;
    sessionStorage.setItem(ssKey, '1');
    if (user) {
      const currentSteps = user.onboarding_steps || [];
      if (currentSteps.includes(stepNum)) return;
      const updated = [...new Set([...currentSteps, stepNum])];
      try {
        await fetch(`${API_URL}/api/user/onboarding`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ steps: updated }) });
        if (refreshUser) refreshUser();
      } catch (e) { /* ignore */ }
    }
  }, [user, refreshUser]);

  useEffect(() => {
    const calc = CALCS.find(c => c.id === active);
    if (calc) document.title = `${calc.label} Gratuito — SAPIENS | finsapiens.it`;
    else document.title = 'Calcolatori Finanziari Gratuiti — SAPIENS | finsapiens.it';
  }, [active]);

  const current = active ? CALCS.find(c => c.id === active) : null;
  const Comp = current?.Component;
  const compProps = {
    ...(active === 'pac' && importoFromUrl ? { initialMonthly: importoFromUrl } : {}),
    ...(current?.step ? { onStepComplete: () => markOnboardingStep(current.step) } : {}),
  };

  const tabCalcs = CALCS.filter(c => c.tab === activeTab);

  return (
    <div className="min-h-screen bg-[#1C1C1A]" data-testid="calcolatori-page">
      <Helmet>
        <title>Calcolatori Finanziari Gratuiti — SAPIENS</title>
        <meta name="description" content="22 calcolatori finanziari gratuiti in italiano: mutuo, PAC, FIRE, investimenti, pensione, auto e molto altro. Calcola e pianifica il tuo futuro finanziario." />
        <link rel="canonical" href="https://finsapiens.it/calcolatori" />
      </Helmet>
      {/* ── TAB NAVIGATION ── */}
      <div style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', background: '#1C1C1A', position: 'sticky', top: 64, zIndex: 30 }}>
        <div className="container-wide">
          <div className="flex overflow-x-auto scrollbar-hide" style={{ gap: 0 }} data-testid="calc-tab-row">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => switchTab(tab.id)}
                className="shrink-0 transition-all"
                style={{
                  padding: '12px 20px',
                  fontSize: 13,
                  fontWeight: activeTab === tab.id ? 500 : 400,
                  color: activeTab === tab.id ? '#E8E8E4' : '#888780',
                  borderBottom: activeTab === tab.id ? '2px solid #C9A84C' : '2px solid transparent',
                  background: 'transparent',
                  whiteSpace: 'nowrap',
                  transition: 'color 200ms, border-color 200ms',
                }}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="container-wide py-8">
        {/* ── CALCULATOR ACTIVE VIEW ── */}
        {current && Comp ? (
          <div>
            <div className="flex items-start justify-between mb-6">
              <div>
                <button onClick={() => { setActive(null); navigate('/calcolatori'); }} className="text-xs text-[#888780] hover:text-[#E8E8E4] mb-3 flex items-center gap-1 transition-colors">
                  ← Torna ai calcolatori
                </button>
                <div className="flex items-center gap-3 mb-1">
                  <span style={{ fontSize: 28 }}>{current.icon}</span>
                  <h1 className="text-2xl font-medium text-[#E8E8E4]">{current.label}</h1>
                </div>
                <p className="text-sm text-[#888780] mt-1">{current.subtitle}</p>
              </div>
              {current.premium && <span className="premium-badge">Premium</span>}
            </div>
            <motion.div key={active} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
              <Comp {...compProps} />
              <CalcResultDisclaimer calc={current} />
            </motion.div>
          </div>
        ) : (
          /* ── CARD GRID ── */
          <div>
            <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }} data-testid={`calc-grid-${activeTab}`}>
              {tabCalcs.map((calc) => (
                <CalcCard key={calc.id} calc={calc} onClick={handleSelect} isPreview={isPreview} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalcolatoriPage;
